import { useTranslation } from 'react-i18next';

import OptoscaleLogoSolo from '@/assets/icons/optoscale_logo_solo.svg?component';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useCage, useCageName } from '@/contexts/cage';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import styles from './SmoltReportHeader.module.scss';

interface SmoltReportHeaderProps {
    isLoading?: boolean;
    createdAt: Date;
    stockingDate: string;
    lastFeedingDate: string | null;
    deliveryDate: string;
    /** The report was computed for the whole tank while a fish group is selected. */
    isCageLevel?: boolean;
}

export default function SmoltReportHeader(props: SmoltReportHeaderProps) {
    const {
        createdAt,
        isLoading = false,
        stockingDate,
        lastFeedingDate,
        deliveryDate,
        isCageLevel = false,
    } = props;

    const { t, i18n } = useTranslation();
    const { location } = useCage();
    const cageName = useCageName();
    const locale = parseLocale(i18n.language);

    const locationNameLabel = `${t('smoltReport.detailView.headerFacility')} ${location?.name}`;
    const cageNameLabel = `${t('smoltReport.detailView.headerTank')} ${cageName ?? ''}`;
    const createdAtLabel = `${t('smoltReport.detailView.headerCreatedAt')} (${formatDateShortLocalized(createdAt, locale)})`;
    const stockingDateLabel = `${t('smoltReport.detailView.headerStockingDate')} (${formatDateShortLocalized(stockingDate, locale)})`;
    const lastFeedingDateLabel = lastFeedingDate
        ? `${t('smoltReport.detailView.headerLastFeedingDate')} (${formatDateShortLocalized(lastFeedingDate, locale)})`
        : null;
    const deliveryDateLabel = `${t('smoltReport.detailView.headerDeliveryDate')} (${formatDateShortLocalized(deliveryDate, locale)})`;

    const subtitleWithDate = [
        locationNameLabel,
        cageNameLabel,
        createdAtLabel,
        stockingDateLabel,
        lastFeedingDateLabel,
        deliveryDateLabel,
    ]
        .filter(Boolean)
        .join(' - ');

    return (
        <div className={styles.headerContent}>
            <div className={styles.logoWrapper}>
                <SvgIcon
                    icon={OptoscaleLogoSolo}
                    size="custom"
                    customSize={50}
                    className={styles.logo}
                />
            </div>
            <div className={styles.textContent}>
                <div className={styles.title}>{t('smoltReport.detailView.headerTitle')}</div>
                {isLoading ? (
                    <SkeletonBox width="100%" height={20} borderRadius={8} />
                ) : (
                    <div className={styles.subtitle}>{subtitleWithDate}</div>
                )}
                {isCageLevel && (
                    <div className={styles.notice}>
                        {t('smoltReport.detailView.cageLevelNotice')}
                    </div>
                )}
            </div>
        </div>
    );
}
